import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { CalendarBlank, ChartBar, UserCircle } from '@phosphor-icons/react';
import { FieldGrid, RowValue, SettingCard, SettingRow } from '../../../../../components/settings/setting-card';
import { Banner } from '../../../../../components/ui/banner';
import { Input } from '../../../../../components/ui/input';
import { Select } from '../../../../../components/ui/select';
import { TextArea } from '../../../../../components/ui/text-area';
import { Toggle } from '../../../../../components/ui/toggle';
import type { StaffAvailabilityPrefs, Weekday } from '@mybusinessmate/schema';
import { Skeleton } from '../../../../../components/ui/skeleton';
import { CLOCK_OPTIONS, WEEKDAYS } from '../../../../../lib/staff-prefs';
import { staffMeQuery, useStaffPreferences } from '../../../../../lib/workspace-api';
import { useQuery } from '@tanstack/react-query';
import { cx } from '../../../../../lib/cx';

export const Route = createFileRoute('/subdomain/admin/_staff/settings/availability')({
  component: AvailabilityPage,
});

const DATE_FORMAT = new Intl.DateTimeFormat('en-IN', { day: 'numeric', month: 'short' });
const shortDate = (iso: string) => (iso ? DATE_FORMAT.format(new Date(iso)) : '');

function AvailabilityPage() {
  const { data: prefs, save } = useStaffPreferences('availability');
  const me = useQuery(staffMeQuery);
  if (!prefs) return <Skeleton className="h-(--mbm-layout-dialog-wide-width) rounded-(--mbm-radius-lg)" />;
  // Your own limit, set on your staff record (an admin can change it in Staff).
  return <AvailabilityForm prefs={prefs} onSave={(next) => save.mutate(next)} maxTasks={me.data?.maxActiveTasks} />;
}

function AvailabilityForm({
  prefs,
  onSave,
  maxTasks,
}: {
  prefs: StaffAvailabilityPrefs;
  onSave: (next: StaffAvailabilityPrefs) => void;
  maxTasks: number | undefined;
}) {
  const update = (patch: Partial<StaffAvailabilityPrefs>) => onSave({ ...prefs, ...patch });
  const oof = prefs.outOfOffice;
  const setOof = (patch: Partial<StaffAvailabilityPrefs['outOfOffice']>) =>
    update({ outOfOffice: { ...oof, ...patch } });

  const toggleDay = (day: Weekday) =>
    update({ days: prefs.days.includes(day) ? prefs.days.filter((d) => d !== day) : [...prefs.days, day] });

  const [adding, setAdding] = useState(false);
  const [skill, setSkill] = useState('');
  const addSkill = () => {
    const name = skill.trim();
    if (name && !prefs.skills.includes(name)) update({ skills: [...prefs.skills, name] });
    setSkill('');
    setAdding(false);
  };

  const awayFrom = shortDate(oof.from);

  return (
    <>
      {oof.on && awayFrom ? (
        <Banner tone="warning" title={`You’re out of office from ${awayFrom}`}>
          {oof.backup ? `New tasks go to ${oof.backup}. ` : ''}A small ‘Away’ badge shows next to your name everywhere.
        </Banner>
      ) : null}

      <SettingCard
        title="Working hours"
        subtitle="Your team and the dispatch board use this to plan your work. Times are in IST."
      >
        <div role="group" aria-label="Working days" className="flex flex-wrap gap-(--mbm-space-2) pb-(--mbm-space-4)">
          {WEEKDAYS.map((d) => {
            const on = prefs.days.includes(d.id);
            return (
              <button
                key={d.id}
                type="button"
                aria-pressed={on}
                onClick={() => toggleDay(d.id)}
                className={cx(
                  'h-(--mbm-size-button-row) rounded-full border px-(--mbm-space-4) text-label-medium focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)',
                  on
                    ? 'border-action-primary bg-action-primary text-text-on-dark'
                    : 'border-border-default bg-surface-card text-text-secondary',
                )}
              >
                {d.label}
              </button>
            );
          })}
        </div>
        <FieldGrid className="sm:max-w-(--mbm-layout-dialog-width)">
          <Select
            variant="filled"
            label="Start"
            value={prefs.start}
            onChange={(e) => update({ start: e.target.value })}
          >
            {CLOCK_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
          <Select variant="filled" label="End" value={prefs.end} onChange={(e) => update({ end: e.target.value })}>
            {CLOCK_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </FieldGrid>
      </SettingCard>

      <SettingCard title="Capacity" subtitle="How much work can land on your plate at once.">
        {/* TODO(api): the live count of tasks in progress needs the tasks API. */}
        <SettingRow
          icon={ChartBar}
          title="Active tasks right now"
          subtitle="Counts tasks in progress or waiting on your fix"
          trailing={<RowValue>— of {maxTasks ?? '—'}</RowValue>}
        />
        <SettingRow
          icon={UserCircle}
          title="Max active tasks"
          subtitle="Set by your admin for your role. Ask them to change it."
          trailing={<RowValue>{maxTasks ?? '—'}</RowValue>}
        />
      </SettingCard>

      <SettingCard title="Out of office" subtitle="Pauses new assignments and tells your team who to go to instead.">
        <SettingRow
          icon={CalendarBlank}
          title="I’m out of office"
          subtitle={
            oof.on && oof.from && oof.to
              ? `Away ${shortDate(oof.from)} – ${shortDate(oof.to)}${oof.backup ? ` · ${oof.backup} covers for you` : ''}`
              : 'Set dates and a backup teammate'
          }
          trailing={<Toggle aria-label="I’m out of office" checked={oof.on} onCheckedChange={(on) => setOof({ on })} />}
        />
        {oof.on ? (
          <div className="flex flex-col gap-(--mbm-space-4) pt-(--mbm-space-2)">
            <FieldGrid className="sm:grid-cols-3">
              <Input
                variant="filled"
                type="date"
                label="From"
                value={oof.from}
                onChange={(e) => setOof({ from: e.target.value })}
              />
              <Input
                variant="filled"
                type="date"
                label="To"
                min={oof.from || undefined}
                value={oof.to}
                onChange={(e) => setOof({ to: e.target.value })}
              />
              {/* TODO(api): a teammate picker needs the staff roster endpoint; for now the name is typed. */}
              <Input
                variant="filled"
                label="Backup teammate"
                value={oof.backup}
                onChange={(e) => setOof({ backup: e.target.value })}
              />
            </FieldGrid>
            <TextArea
              variant="filled"
              label="Away note (your team sees this)"
              rows={3}
              value={oof.note}
              onChange={(e) => setOof({ note: e.target.value })}
            />
          </div>
        ) : null}
      </SettingCard>

      <SettingCard title="Skills" subtitle="Helps leads pick the right person when they add people to a task.">
        <div className="flex flex-wrap items-center gap-(--mbm-space-2)">
          {prefs.skills.map((s) => (
            <span
              key={s}
              className="inline-flex h-(--mbm-size-button-row) items-center rounded-full bg-surface-tint px-(--mbm-space-4) text-label-medium text-text-primary"
            >
              {s}
            </span>
          ))}
          {adding ? (
            <input
              autoFocus
              aria-label="New skill"
              value={skill}
              maxLength={40}
              onChange={(e) => setSkill(e.target.value)}
              onBlur={addSkill}
              onKeyDown={(e) => {
                if (e.key === 'Enter') addSkill();
                if (e.key === 'Escape') {
                  setSkill('');
                  setAdding(false);
                }
              }}
              className="h-(--mbm-size-button-row) rounded-full border border-border-default bg-surface-card px-(--mbm-space-4) text-label-medium outline-none focus:outline-2 focus:-outline-offset-2 focus:outline-focus-ring"
            />
          ) : (
            <button
              type="button"
              onClick={() => setAdding(true)}
              className="inline-flex h-(--mbm-size-button-row) items-center rounded-full border border-border-default px-(--mbm-space-4) text-label-medium text-text-secondary hover:text-text-primary focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
            >
              + Add skill
            </button>
          )}
        </div>
      </SettingCard>
    </>
  );
}
